import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { api } from "./api";
import { ProgramActivitySession, ProgramActivitySessionEnrollment } from "../models/programs.models";
import { ProgramRegistration } from "../models/programregistration.models";

export interface ProgramActivitySessionEnrollmentRegistration {
	ProgramActivitySessionEnrollment: ProgramActivitySessionEnrollment;
	Registration: ProgramRegistration;
}

export const useProgramActivitySessionsByProgramIdQuery = (programId: number, dateFrom: string, dateTo: string) => {
	const hasAccess = useAccess("role.user.readonly");
	const accountId = useAccountId();

	return useQuery(['program-activity-sessions-by-program-id', programId, dateFrom, dateTo, accountId], async () => {
		let res = await api.get<ProgramActivitySession[]>(`/admin/programs/${programId}/activitysessions`, { params: { dateFrom: dateFrom, dateTo: dateTo }});
		return res.data;
	}, { staleTime: Infinity, cacheTime: 60000, enabled: hasAccess && programId > 0 });
}

export const useProgramActivitySessionByIdQuery = (programActivitySessionId: number) => {
	const hasAccess = useAccess("role.user.readonly");
	const accountId = useAccountId();

	return useQuery(['program-activity-session-by-id', programActivitySessionId, accountId], async () => {
		let res = await api.get<ProgramActivitySession>(`/admin/programs/activities/sessions/${programActivitySessionId}`);
		return res.data;
	}, { staleTime: Infinity, cacheTime: 60000, enabled: hasAccess && programActivitySessionId > 0 });
}

export const useProgramActivitySessionEnrollmentsByIdQuery = (programActivitySessionId: number, autoRefetch: boolean = false) => {
	const hasAccess = useAccess("role.user.readonly");
	const accountId = useAccountId();

	return useQuery(['program-activity-session-enrollments-by-id', programActivitySessionId, accountId], async () => {
		let res = await api.get<ProgramActivitySessionEnrollmentRegistration[]>(`/admin/programs/activities/sessions/${programActivitySessionId}/enrollments`);
		return res.data;
	}, { staleTime: Infinity, cacheTime: 60000, enabled: hasAccess && programActivitySessionId > 0, refetchInterval: autoRefetch ? 30000 : false });
}

export const useProgramActivitySessionEnrollmentsByRegistrationIdQuery = (registrationId: number) => {
	const hasAccess = useAccess("role.user.readonly");
	const accountId = useAccountId();

	return useQuery(['program-activity-session-enrollments-by-registration-id', registrationId, accountId], async () => {
		let res = await api.get<ProgramActivitySessionEnrollment[]>(`/admin/programs/sessions/registration/${registrationId}/enrollments`);
		return res.data;
	}, { staleTime: Infinity, cacheTime: 60000, enabled: hasAccess && registrationId > 0 });
}


export const useProgramActivitySessionEnrollmentCheckInMutation = (programActivitySessionId: number) => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation((model: { programActivitySessionEnrollmentId: number, checkedIn: boolean }) => {
		return api.post<ProgramActivitySessionEnrollment>(`/admin/programs/activities/sessions/enrollments/${model.programActivitySessionEnrollmentId}/checkin`, { checkedIn: model.checkedIn });
	}, {
		onMutate: async (model) => {
			const queryKey = ['program-activity-session-enrollments-by-id', programActivitySessionId, accountId] as const;
			await queryClient.cancelQueries(queryKey);

			const previousData = queryClient.getQueryData<ProgramActivitySessionEnrollmentRegistration[]>(queryKey);
			queryClient.setQueryData<ProgramActivitySessionEnrollmentRegistration[]>(queryKey, (current) => {
				if (!current)
					return [];

				return current.map((enrollment) => {
					if (enrollment.ProgramActivitySessionEnrollment.Id !== model.programActivitySessionEnrollmentId)
						return enrollment;

					return {
						...enrollment,
						ProgramActivitySessionEnrollment: {
							...enrollment.ProgramActivitySessionEnrollment,
							CheckedIn: model.checkedIn
						}
					};
				});
			});

			return { queryKey, previousData };
		},
		onError: (_error, _model, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(context.queryKey, context.previousData);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries(['program-activity-session-enrollments-by-id', programActivitySessionId, accountId]);
		}
	});
}

export const useProgramActivitySessionEnrollmentCheckOutMutation = (programActivitySessionId: number) => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation((model: { programActivitySessionEnrollmentId: number, checkedOut: boolean }) => {
		return api.post<ProgramActivitySessionEnrollment>(`/admin/programs/activities/sessions/enrollments/${model.programActivitySessionEnrollmentId}/checkout`, { checkedOut: model.checkedOut });
	}, {
		onMutate: async (model) => {
			const queryKey = ['program-activity-session-enrollments-by-id', programActivitySessionId, accountId] as const;
			await queryClient.cancelQueries(queryKey);

			const previousData = queryClient.getQueryData<ProgramActivitySessionEnrollmentRegistration[]>(queryKey);
			queryClient.setQueryData<ProgramActivitySessionEnrollmentRegistration[]>(queryKey, (current) => {
				if (!current)
					return [];

				return current.map((enrollment) => {
					if (enrollment.ProgramActivitySessionEnrollment.Id !== model.programActivitySessionEnrollmentId)
						return enrollment;

					return {
						...enrollment,
						ProgramActivitySessionEnrollment: {
							...enrollment.ProgramActivitySessionEnrollment,
							CheckedOut: model.checkedOut
						}
					};
				});
			});

			return { queryKey, previousData };
		},
		onError: (_error, _model, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(context.queryKey, context.previousData);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries(['program-activity-session-enrollments-by-id', programActivitySessionId, accountId]);
		}
	});
}